import React, { useEffect, useState } from "react";
import {
    Space,
    Divider,
    Row,
    Col,
    Typography,
    Tooltip
} from "antd";
import LoadingButton from '@mui/lab/LoadingButton';
import ArrowRightOutlined from "@ant-design/icons/ArrowRightOutlined";
import GenericOnClickModal from "../../buttons/genericOnClickModal";
import { backendPostWithoutRefresh } from "../../../helpers/callBackend";
import CurrencySingleValue from "../currency/singleValue";

function BankAccountBalanceModal(props) {
    const [refreshBalanceLoading, setRefreshBalanceLoading] = useState(false);

    const forceRefreshBalance = () => {
        if (props.bankAccountId === undefined) return;
        setRefreshBalanceLoading(true);
        backendPostWithoutRefresh("/api/bank-account/update-cache/", {
            accountId: props.bankAccountId,
            forceDynamicRefresh: true,
        }).then((res) => {
            props.setBankAccountData(res.data?.updated_account);
            setRefreshBalanceLoading(false);
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Never";
        const date = new Date(dateString);

        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;

        return `${month}/${day}/${year} at ${hours}:${minutes} ${ampm}`;
    };

    const renderField = (label, dateStr) => {
        if (!dateStr) return null;
        return (
            <Row>
                <Col span={24}>
                    <Tooltip title={formatDate(dateStr)}>
                        <Space>
                            <Typography.Text strong>{label}:</Typography.Text>
                            <Typography.Text>{formatDate(dateStr)}</Typography.Text>
                        </Space>
                    </Tooltip>
                </Col>
            </Row>
        );
    };

    const renderRefreshInfo = () => {
        if (!props.bankInfo || !props.bankInfo.source_account) {
            return <></>;
        }

        const {
            source_account: {
                last_attempt,
                last_api_aggregation_success,
                last_api_aggregation_attempt,
                last_expected_transaction
            }
        } = props.bankInfo;

        const lastCurrentBalanceFetch = props.bankInfo.last_current_balance_fetch;
        const lastTransactionRefresh = props.bankInfo.last_fetch;

        return (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Divider style={{ margin: "10px 0px" }} />
                <Typography.Title level={5}>Refresh Information</Typography.Title>
                {renderField("Last Balance Refresh", lastCurrentBalanceFetch)}
                {false && renderField("Last Transaction Refresh", lastTransactionRefresh)}
                {renderField("Expected Transactions Until", last_expected_transaction)}
                {renderField("Last Refresh Request to Data Provider", last_attempt)}
                {renderField("Last Bank Update to Data Provider", last_api_aggregation_success)}
                {renderField("Last Bank Update Attempt by Data Provider", last_api_aggregation_attempt)}
            </Space>
        );
    };

    return (
        <GenericOnClickModal
            size="md"
            button={<>View details <ArrowRightOutlined /></>}
            modalTitle="How are balances calculated?"
            modalBody={
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Row>
                        <Typography.Text strong>
                            Your available balance is the portion of your
                            balance available to spend. Your current
                            balance is your available balance plus funds
                            that are being held. If you have related ICS balances,
                            this is added into your aggregated balance.
                        </Typography.Text>
                    </Row>
                    <Row justify="space-between">
                        <Col>Available Balance</Col>
                        <Col>
                            <CurrencySingleValue
                                currency={props.currency || "USD"}
                                currencySymbol={props.currencySymbol || "$"}
                                amount={props.availableBalance}
                            />
                        </Col>
                    </Row>
                    <Row justify="space-between">
                        <Col>Current Balance</Col>
                        <Col>
                            <CurrencySingleValue
                                currency={props.currency || "USD"}
                                currencySymbol={props.currencySymbol || "$"}
                                amount={props.currentBalance}
                            />
                        </Col>
                    </Row>
                    {props.ICSBalance ? (
                        <>
                            <Row justify="space-between">
                                <Col>Related Accessible Balance</Col>
                                <Col>
                                    <CurrencySingleValue
                                        currency={props.currency || "USD"}
                                        currencySymbol={props.currencySymbol || "$"}
                                        amount={props.ICSBalance}
                                    />
                                </Col>
                            </Row>
                            <Divider style={{ margin: "5px 0px" }} />
                            <Row justify="space-between">
                                <Col>Aggregated Balance</Col>
                                <Col>
                                    <CurrencySingleValue
                                        currency={props.currency || "USD"}
                                        currencySymbol={props.currencySymbol || "$"}
                                        amount={props.aggregatedBalance}
                                    />
                                </Col>
                            </Row>
                        </>
                    ) : null}

                    {renderRefreshInfo()}
                </Space>
            }
            modalFooter={
                <Row justify="space-between">
                    <Col span={16} align="left">
                        {props.lastBalanceRefreshComponent}
                    </Col>
                    <Col>
                        <LoadingButton
                            disableElevation
                            fullWidth
                            size="small"
                            type="submit"
                            variant="outlined"
                            color="secondary"
                            onClick={forceRefreshBalance}
                            loadingPosition="start"
                            loading={refreshBalanceLoading}
                            sx={{ fontSize: 12, width: "100px", textTransform: 'none' }}
                        >
                            Refresh
                        </LoadingButton>
                    </Col>
                </Row>
            }
        />
    );
};

export default BankAccountBalanceModal;
